import {createStyles, Group, Select, Text} from '@mantine/core';
import React from 'react';

import {AntibodyEnforcedSchemaFieldSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import MigrationPreviewSection from './MigrationPreviewSection';

// Most enforced fields are automatically filled in by NDC, so we don't want to allow arbitrary migrated
// data in them. Dropdowns (aka selectors) are OK because they have a limited set of options, and we plan
// to let the schema admin explicitly choose how to migrate each option.
const MIGRATABLE_FIELD_TYPES = new Set([FieldType.SELECTOR]);

type DropdownFromSpec = NonNullable<
  AntibodyEnforcedSchemaFieldSpec['dropdown']
>['compatibleDropdowns'][number];

export interface NewFieldsMigrationPreviewProps {
  fieldsToCreate: Array<ClientSchemaField>;
  enforcedFieldSpecs: Array<AntibodyEnforcedSchemaFieldSpec>;
  existingFields: Array<SchemaField | ClientSchemaField>;
  fieldMigrationMappings: Array<FieldMigrationMapping>;
  onMappingsChange: React.Dispatch<React.SetStateAction<Array<FieldMigrationMapping>>>;
}

const useStyles = createStyles(() => ({
  definitionCell: {
    verticalAlign: 'middle',
  },
  iconStyle: {
    color: GRAY_DARKER,
    flexShrink: 0,
    height: '12px',
    width: '12px',
  },
}));

function collateCompatibleDropdowns(
  enforcedFieldSpecs: Array<AntibodyEnforcedSchemaFieldSpec>
): Array<DropdownFromSpec> {
  const allDropdowns = enforcedFieldSpecs.map((spec) => spec.dropdown?.compatibleDropdowns ?? []).flat();
  const dropdownsById = allDropdowns.reduce<Record<string, DropdownFromSpec>>((acc, dropdown) => {
    if (dropdown != null) {
      acc[dropdown.id] = dropdown;
    }
    return acc;
  }, {});
  return Object.values(dropdownsById);
}

function speccedDropdownToSchemaFieldSelector(dropdown: DropdownFromSpec): SchemaFieldSelector {
  return {
    id: dropdown.id,
    name: dropdown.name,
    schemaFieldSelectorOptions: dropdown.schemaFieldSelectorOptions.map((option) => ({
      id: option.id,
      name: option.name,
      archiveRecord: option.archiveRecord,
    })),
    allSchemaFieldSelectorOptions: dropdown.allSchemaFieldSelectorOptions.map((option) => ({
      id: option.id,
      name: option.name,
      archiveRecord: option.archiveRecord,
    })),
    archiveRecord: dropdown.archiveRecord,
  };
}

function getFieldTypeLabel(
  field: ClientSchemaField,
  schemaFieldSelectors: Array<SchemaFieldSelector>,
  classes: ReturnType<typeof useStyles>['classes']
): JSX.Element {
  const {icon, label} = getIconAndLabel({
    field,
    schemaFieldSelectors,
    inEditMode: false,
  });
  return (
    <Group spacing="xs" align="center" noWrap={true}>
      <BenchlingIcon icon={icon} className={classes.iconStyle} />
      {label}
    </Group>
  );
}

export default function NewFieldsMigrationPreview({
  fieldsToCreate,
  enforcedFieldSpecs,
  existingFields,
  fieldMigrationMappings,
  onMappingsChange,
}: NewFieldsMigrationPreviewProps): JSX.Element {
  const {classes} = useStyles();
  const schemaFieldSelectors = collateCompatibleDropdowns(enforcedFieldSpecs).map((dropdown) =>
    speccedDropdownToSchemaFieldSelector(dropdown)
  );
  if (fieldsToCreate.length === 0) {
    return (
      <MigrationPreviewSection title="New fields">
        <Text size="sm" color="dimmed">
          No new fields will be added.
        </Text>
      </MigrationPreviewSection>
    );
  }

  return (
    <MigrationPreviewSection title="New fields">
      <Table numRows={fieldsToCreate.length}>
        <Column header="Field name" renderCell={(i) => fieldsToCreate[i].name} />
        <Column
          header="Definition"
          renderCell={(i) => (
            <Cell className={classes.definitionCell}>
              {getFieldTypeLabel(fieldsToCreate[i], schemaFieldSelectors, classes)}
            </Cell>
          )}
        />
        <Column header="Validation" renderCell={(i) => (fieldsToCreate[i].isRequired ? 'Required' : '')} />
        <Column
          header="Source field"
          renderCell={(i) => {
            const destField = fieldsToCreate[i];
            if (!MIGRATABLE_FIELD_TYPES.has(destField.fieldType)) {
              return null;
            }
            const matchingFields = existingFields.filter(
              (f) => f.fieldType === destField.fieldType && f.apiId != null && f.archiveRecord == null
            );
            const currentMapping = fieldMigrationMappings.find(
              (m) => m.destinationFieldName === destField.name
            );
            return (
              <Cell>
                <Select
                  aria-label={`Source field for ${destField.name}`}
                  data={matchingFields.map((f) => ({value: f.apiId!, label: f.displayName}))}
                  value={currentMapping?.sourceFieldId ?? null}
                  onChange={(val) => {
                    onMappingsChange((prev) => {
                      const without = prev.filter((m) => m.destinationFieldName !== destField.name);
                      return val != null
                        ? [...without, {sourceFieldId: val, destinationFieldName: destField.name}]
                        : without;
                    });
                  }}
                  clearable={true}
                  placeholder="None"
                  disabled={matchingFields.length === 0}
                />
              </Cell>
            );
          }}
        />
      </Table>
    </MigrationPreviewSection>
  );
}
